/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { useMemo, type FC } from 'react'
import { Button, Col, FloatingLabel, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'


const validationSchema = yup.object().shape({
  description: yup.string()
    .min(1, 'O setor deve ter no mínimo 1 caractere')
    .max(255, 'O setor deve ter no máximo 255 caracteres')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormJobRequestEventHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormJobRequestEventHandleSubmit
  initialValues?: Partial<ValidationSchema>
  isLoading?: boolean
  readOnly?: boolean
}

/**
 * FormJobRequestEvent is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormJobRequestEvent from './src/views/PostEdit/FormJobRequestEvent.tsx'
 *
 * const MyComponent = () => (
 *   <FormJobRequestEvent />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormJobRequestEvent: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false,
  readOnly = false,
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik.values, formik.initialValues)
  }, [formik.values, formik.initialValues])

  return (
    <Form onSubmit={formik.handleSubmit}>
      <Row style={{ paddingBottom: 20 }}>
        <Form.Group as={Col} md={12} className='mb-3'>
          <FloatingLabel label="Deixe uma anotação para a solicitação de vaga." className="mb-3">
            <Form.Control
              as="textarea"
              rows={1}
              name='description'
              maxLength={3000}
              style={{ height: 'auto' }}
              value={formik?.values?.description ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={formik.isSubmitting || isLoading || readOnly}
            />
          </FloatingLabel>
        </Form.Group>
        <Col className='mb-3' md={12}>
          <Button
            type='submit'
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Comentar
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default FormJobRequestEvent
